import { MAX_SEARCH_TERMS_BYTES } from '../constants.js';
import { getByteSize } from '../byte-counter.js';
import {
  resolveItemHighlights,
  resolveProductTitle,
} from '../interaction/builders/listing-draft-normalizers.js';

const SECTION_LABELS = {
  title: '标题',
  bullets: '五点',
  description: '描述',
  searchTerms: '搜索词',
  qa: 'Q&A',
};

const VERSION_LABELS = ['A', 'B', 'C'];

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function cleanText(value) {
  return String(value || '').trim();
}

function clampTitleText(value, limit = 75) {
  const raw = cleanText(value).replace(/\s+/g, ' ');
  if (!raw || raw.length <= limit) return raw;
  let out = raw.slice(0, limit).trimEnd();
  const lastSpace = out.lastIndexOf(' ');
  if (lastSpace > Math.max(12, limit - 20)) {
    out = out.slice(0, lastSpace).trimEnd();
  }
  return out.replace(/[|.,;:!\-]+$/g, '').trim();
}

function sectionLabel(section) {
  return SECTION_LABELS[section] || cleanText(section) || '未知';
}

function normalizeCandidateText(candidate) {
  if (typeof candidate === 'string') return cleanText(candidate);
  if (!candidate || typeof candidate !== 'object') return '';
  return cleanText(candidate.keyword || candidate.term || candidate.text || candidate.value || candidate.word || candidate['关键词']);
}

function normalizeTextList(values) {
  return asArray(values).map(cleanText).filter(Boolean);
}

function normalizeSearchTerms(searchTerms) {
  const directText = cleanText(searchTerms?.searchTermsEn);
  if (directText) return directText;
  return asArray(searchTerms?.candidates).map(normalizeCandidateText).filter(Boolean).join(' ');
}

function normalizeDescriptionParagraphs(text) {
  const value = cleanText(text);
  if (!value) return [];
  if (value.includes('|||')) return value.split('|||').map(cleanText).filter(Boolean);
  if (value.includes('\n')) return value.split(/\n+/).map(cleanText).filter(Boolean);
  return [value];
}

function titleOptionText(option) {
  return clampTitleText(resolveProductTitle(option));
}

function normalizeTitle(title, selectedTitleVersion = 0) {
  const hasError = !!title?.error;
  const rawOptions = asArray(title?.titleOptions);
  const fallbackTitle = clampTitleText(resolveProductTitle(title));
  const options = rawOptions
    .map((option, index) => {
      const text = titleOptionText(option) || fallbackTitle;
      if (!text) return null;
      const version = cleanText(option?.version || VERSION_LABELS[index] || `V${index + 1}`);
      const itemHighlights = resolveItemHighlights(option, title);
      return {
        index,
        version,
        label: `版本 ${version}`,
        text,
        productTitle: text,
        itemHighlights,
        // Presentation aliases remain available to unchanged DOM/CSS consumers.
        highlight: itemHighlights,
        formula: cleanText(option?.formula),
        tags: normalizeTextList(option?.tags),
        charCount: text.length,
        mobilePreview: clampTitleText(option?.mobilePreview || text),
      };
    })
    .filter(Boolean);

  const safeSelectedIndex = Math.max(0, Math.min(Number(selectedTitleVersion || 0), Math.max(options.length - 1, 0)));
  const selected = options[safeSelectedIndex] || null;
  const primaryText = selected?.text || fallbackTitle;
  const itemHighlights = resolveItemHighlights(selected, title);

  return {
    isAvailable: !hasError && !!primaryText,
    error: hasError ? cleanText(title.error) : '',
    primaryText: hasError ? '' : primaryText,
    productTitle: hasError ? '' : primaryText,
    selectedIndex: selected ? safeSelectedIndex : 0,
    selectedVersionLabel: selected?.label || '',
    itemHighlights,
    highlight: itemHighlights,
    highlightCharCount: itemHighlights.length,
    options,
    charCount: primaryText.length,
  };
}

function normalizeBullets(bullets) {
  const items = normalizeTextList(bullets?.bulletsEn);
  return {
    isAvailable: !bullets?.error && items.length > 0,
    error: bullets?.error ? cleanText(bullets.error) : '',
    items: items.map((text, index) => ({
      index,
      label: `B${index + 1}`,
      text,
      byteCount: getByteSize(text),
    })),
    count: items.length,
  };
}

function normalizeDescription(description) {
  const text = cleanText(description?.descriptionEn);
  const paragraphs = normalizeDescriptionParagraphs(text);
  return {
    isAvailable: !description?.error && paragraphs.length > 0,
    error: description?.error ? cleanText(description.error) : '',
    text,
    paragraphs,
    charCount: text.length,
  };
}

function normalizeAplusModules(description) {
  return asArray(description?.aplusModules)
    .filter((module) => module && typeof module === 'object')
    .map((module, index) => ({
      index,
      moduleType: cleanText(module.moduleType) || '产品描述',
      headline: cleanText(module.headline),
      bodyText: cleanText(module.bodyText),
      imageDirection: cleanText(module.imageDirection),
    }));
}

function normalizeSearchTermsModel(searchTerms) {
  const text = normalizeSearchTerms(searchTerms);
  const byteCount = getByteSize(text);
  return {
    isAvailable: !searchTerms?.error && !!text,
    error: searchTerms?.error ? cleanText(searchTerms.error) : '',
    text,
    charCount: text.length,
    byteCount,
    byteLimit: MAX_SEARCH_TERMS_BYTES,
    isOverLimit: byteCount > MAX_SEARCH_TERMS_BYTES,
  };
}

function normalizeQa(qa) {
  const items = asArray(qa)
    .filter((item) => item && typeof item === 'object')
    .map((item, index) => ({
      index,
      questionEn: cleanText(item.questionEn),
      answerEn: cleanText(item.answerEn),
      questionZh: cleanText(item.questionZh),
      answerZh: cleanText(item.answerZh),
    }))
    .filter((item) => item.questionEn || item.answerEn || item.questionZh || item.answerZh);

  return {
    isAvailable: items.length > 0,
    items,
    count: items.length,
  };
}

function normalizeV1Title(v1) {
  const title = v1?.title;
  return cleanText(resolveProductTitle(title, title?.titleOptions?.[0]));
}

function normalizeV1Bullets(v1) {
  const bullets = v1?.bullets;
  if (Array.isArray(bullets)) return normalizeTextList(bullets);
  return normalizeTextList(bullets?.bulletsEn);
}

function normalizeV1Description(v1) {
  const description = v1?.description;
  if (typeof description === 'string') return cleanText(description);
  return cleanText(description?.descriptionEn);
}

function buildV1Compare({ listingDraftV1, title, description }) {
  const v1Title = normalizeV1Title(listingDraftV1);
  const v1Bullets = normalizeV1Bullets(listingDraftV1);
  const v1Description = normalizeV1Description(listingDraftV1);

  return {
    title: {
      shouldShow: !!v1Title && v1Title !== title.primaryText,
      text: v1Title,
    },
    bullets: {
      shouldShow: v1Bullets.length > 0,
      items: v1Bullets,
    },
    description: {
      shouldShow: !!v1Description && v1Description !== description.text && Math.abs(v1Description.length - description.text.length) > 5,
      text: v1Description,
    },
  };
}

function normalizeMissingSections(generationMeta) {
  return asArray(generationMeta?.readiness?.missing).map((key) => ({
    key,
    label: sectionLabel(key),
  }));
}

function normalizeIssueItems(sectionErrors) {
  return asArray(sectionErrors)
    .filter((item) => item && typeof item === 'object')
    .map((item) => ({
      section: cleanText(item.section),
      label: sectionLabel(item.section),
      type: cleanText(item.type) || 'error',
      message: cleanText(item.message),
    }))
    .filter((item) => item.message);
}

function statusLabel(status) {
  if (status === 'partial') return '部分生成';
  if (status === 'failed') return '生成失败';
  if (status === 'running' || status === 'queued') return '生成中';
  return '完整生成';
}

function resolveCanProceedToCompliance(generationMeta, titleModel, bulletsModel) {
  if (generationMeta?.readiness?.canProceedToCompliance !== undefined) {
    return generationMeta.readiness.canProceedToCompliance !== false;
  }
  return titleModel.isAvailable && bulletsModel.count >= 5;
}

function resolveCanProceedToFullTextReview(generationMeta, titleModel, bulletsModel) {
  if (generationMeta?.readiness?.canProceedToFullTextReview !== undefined) {
    return generationMeta.readiness.canProceedToFullTextReview !== false;
  }
  return titleModel.isAvailable && bulletsModel.count >= 5;
}

export function buildListingResultViewModel({
  title,
  bullets,
  description,
  searchTerms,
  qa,
  selectedTitleVersion = 0,
  listingDraftV1,
  generationMeta,
  sectionErrors,
} = {}) {
  const titleModel = normalizeTitle(title, selectedTitleVersion);
  const bulletsModel = normalizeBullets(bullets);
  const descriptionModel = normalizeDescription(description);
  const searchTermsModel = normalizeSearchTermsModel(searchTerms);
  const qaModel = normalizeQa(qa);
  const aplusModules = normalizeAplusModules(description);
  const missingSections = normalizeMissingSections(generationMeta);
  const issueItems = normalizeIssueItems(sectionErrors);

  return {
    hasContent: titleModel.isAvailable || bulletsModel.isAvailable || descriptionModel.isAvailable || searchTermsModel.isAvailable || qaModel.isAvailable || aplusModules.length > 0,
    status: cleanText(generationMeta?.status) || 'complete',
    statusLabel: statusLabel(generationMeta?.status),
    canProceedToCompliance: resolveCanProceedToCompliance(generationMeta, titleModel, bulletsModel),
    canProceedToFullTextReview: resolveCanProceedToFullTextReview(generationMeta, titleModel, bulletsModel),
    missingSections,
    issueItems,
    title: titleModel,
    bullets: bulletsModel,
    description: descriptionModel,
    aplusModules,
    searchTerms: searchTermsModel,
    qa: qaModel,
    v1Compare: buildV1Compare({
      listingDraftV1,
      title: titleModel,
      description: descriptionModel,
    }),
  };
}
